import React, { useState } from "react";
import RegistrationInviteModal from "./components/StandupInviteModal";
import "./App.css";
import { BrowserRouter, Routes, Route, useParams } from "react-router-dom";
import { VideoProvider } from "./store/VideoContext";
import ThemeProvider, { useTheme } from "./contexts/ThemeContext";
import { LanguageProvider, useLanguage } from "./contexts/LanguageContext";
import Header from "./components/Header";
import Hero from "./components/Hero";
import ContentRow from "./components/ContentRow";
import MovieModal from "./components/MovieModal";
import ThemeSidebar from "./components/ThemeSidebar";
import SimpleApiTest from "./components/SimpleApiTest";
import MayanSDKContainer from "./components/MayanSDKContainer";
import { useMultipleVideoCategories } from "./hooks/useVideos";
import {
  featuredMovie,
  trendingNow,
  popularMovies,
  newReleases,
  myList,
  continueWatching,
} from "./mock/movieData";

// Mode de test pour l'API - changez à true pour tester l'API réelle
const USE_REAL_API = true; // Activé pour tester
const USE_SIMPLE_TEST = false; // Désactivé pour utiliser l'app normale avec l'API

const Home = () => {
  const [selectedMovie, setSelectedMovie] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Utiliser le contexte de thème
  const { themeConfig, labels, currentTheme, filteredLabels, isSidebarOpen } =
    useTheme();
  
  // Utiliser le contexte de langue pour forcer le rechargement lors du changement
  const { language } = useLanguage();

  // Extraire les labels de musique comme catégories basées sur le thème actuel
  const themeCategories = React.useMemo(
    () => filteredLabels.map((item) => item.label),
    [filteredLabels]
  );

  // Mémoriser les catégories pour éviter les re-renders
  const allCategories = React.useMemo(
    () => [
      "trending",
      "popular",
      "new-releases",
      "featured",
      "my-list",
      "continue-watching",
      ...themeCategories, // Ajouter les labels comme catégories selon le thème
      "sport", // Ajouter le thème sport
    ],
    [themeCategories]
  );

  // Utiliser l'API pour récupérer les vidéos par catégories (incluant les labels de musique)
  const { data, isAnyLoading, refreshAllCategories } = useMultipleVideoCategories(allCategories, {
    theme: currentTheme,
    language: language, // Passer la langue actuelle au hook
  });
    const {pathseo = null} = useParams();

  // Show modal if ?d=xxxxx is present in URL and matches a movie (only once per param change, allow closing)
  const autoModalRef = React.useRef(false);
  React.useEffect(() => {
    if (!pathseo || !data || autoModalRef.current) return;
    // Flatten all videos from all categories
    const allVideos = Object.values(data).flatMap((cat) => cat?.videos || []);
    const found = allVideos.find((v) => v.pathseo === pathseo);
    if (found) {
      setSelectedMovie(found);
      setIsModalOpen(true);
      autoModalRef.current = true;
    }
    // Only run once per param change
  }, [data, window.location.search]);
  // Reset autoModalRef if param changes
  React.useEffect(() => {
    autoModalRef.current = false;
  }, [window.location.search]);

  // Debug: Afficher les données récupérées
  React.useEffect(() => {
    // console.log('📊 Données API récupérées:', data);
    // console.log('📊 Catégories demandées:', themeCategories);

    if (data) {
      Object.entries(data).forEach(([category, categoryData]) => {
        // console.log(`📁 ${category}: ${categoryData?.videos?.length || 0} vidéos`);
      });
    }
  }, [data, themeCategories]);

  React.useEffect(() => {
    const html = document.documentElement;
    const body = document.body;

    if (isSidebarOpen) {
      html.style.overflow = "hidden"; // Désactiver le scroll global
      body.style.overflow = "hidden";
    } else {
      html.style.overflow = "";
      body.style.overflow = "";
    }

    return () => {
      html.style.overflow = "";
      body.style.overflow = "";
    };
  }, [isSidebarOpen]);

  const handleMovieClick = (movie) => {
    setSelectedMovie(movie);
    setIsModalOpen(true);
  };

  const handleSearchChange = (query) => {
    setSearchQuery(query);
  };

  // Fonction pour filtrer les vidéos par recherche (non sensible à la casse, recherche partielle)
  const filterVideosBySearch = (videos, query) => {
    if (!query || !videos) return videos;

    const searchTerm = query.toLowerCase().trim();
    if (!searchTerm) return videos;

    return videos.filter((video) => {
      const title = (video.title || video.name || "").toLowerCase();
      const overview = (video.overview || "").toLowerCase();
      const channelTitle = (
        video.channelTitle ||
        video.artist ||
        video.label ||
        ""
      ).toLowerCase();

      return (
        title.includes(searchTerm) ||
        overview.includes(searchTerm) ||
        channelTitle.includes(searchTerm)
      );
    });
  };

  // Récupérer les données depuis l'API ou utiliser les données mock en cas d'erreur
  const rawTrendingData = data?.trending?.videos || trendingNow;
  const rawPopularData = data?.popular?.videos || popularMovies;
  const rawNewReleasesData = data?.["new-releases"]?.videos || newReleases;

  // Appliquer le filtre de recherche
  const trendingData = filterVideosBySearch(rawTrendingData, searchQuery);
  const popularData = filterVideosBySearch(rawPopularData, searchQuery);
  const newReleasesData = filterVideosBySearch(rawNewReleasesData, searchQuery);

  // Utiliser la toute dernière vidéo créée comme featuredContent
  const getLatestVideo = () => {
    // Flatten all videos from all categories
    const allVideos = Object.values(data || {}).flatMap(
      (cat) => cat?.videos || []
    );
    if (allVideos.length === 0) return featuredMovie;
    // Sort by release_date or first_air_date descending
    const sorted = allVideos.slice().sort((a, b) => {
      const dateA = new Date(a.release_date || a.first_air_date || 0);
      const dateB = new Date(b.release_date || b.first_air_date || 0);
      return dateB - dateA;
    });
    return sorted[0] || featuredMovie;
  };
  const featuredData = getLatestVideo();
  const rawMyListData = data?.["my-list"]?.videos || myList;
  const rawContinueWatchingData =
    data?.["continue-watching"]?.videos || continueWatching;

  // Appliquer le filtre de recherche aux autres catégories
  const myListData = filterVideosBySearch(rawMyListData, searchQuery);
  const continueWatchingData = filterVideosBySearch(
    rawContinueWatchingData,
    searchQuery
  );

  if (isAnyLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-xl">Chargement des vidéos...</div>
      </div>
    );
  }

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedMovie(null);
  };

  // Si le mode test API est activé, afficher le composant de test
  if (USE_REAL_API && USE_SIMPLE_TEST) {
    return (
      <div className="min-h-screen bg-black">
        <Header />
        <SimpleApiTest />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black overflow-visible">
      {/* Header */}
      <Header
        onSearchChange={handleSearchChange}
        searchQuery={searchQuery}
        onInviteClick={() => setIsInviteModalOpen(true)}
        currentTheme={currentTheme}
      />

      {/* Hero Section */}
      <Hero featuredContent={featuredData} onMovieClick={handleMovieClick} />

      {/* Content Rows */}
      <div
        id="all-content-rows"
        className="relative z-10"
        style={{ overflow: "visible" }}
      >
        {/* Latest Videos Row - published today or yesterday */}
        {(() => {
          // Collect all videos from all categories
          const allVideos = Object.values(data || {}).flatMap(
            (cat) => cat?.videos || []
          );
          // Filter videos published in the last 2 days
          const now = new Date();
          const cutoff = new Date(now.getTime() - 2 * 86400000); // 2 days ago

          // filter and take the last 4 videos
          const latestVideos = allVideos
            .filter((video) => {
              const publishedStr = video.publishedAt || video.date;
              if (!publishedStr) return false;
              const publishedDate = new Date(publishedStr);
              return publishedDate >= cutoff;
            })
            .slice(-4);
          if (latestVideos.length === 0) return null;
          return (
            <ContentRow
              title={`Dernières vidéos publiées (${latestVideos.length})`}
              movies={latestVideos}
              onMovieClick={handleMovieClick}
              isModalOpen={isModalOpen}
            />
          );
        })()}
        
        {/* Mayan SDK Container */}
        <MayanSDKContainer />
        
        {/* Sections principales avec recherche */}
        {trendingData.length > 0 && (
          <ContentRow
            title={`Trending${
              searchQuery
                ? ` (${trendingData.length} result${
                    trendingData.length > 1 ? "s" : ""
                  })`
                : ""
            }`}
            movies={trendingData}
            onMovieClick={handleMovieClick}
            isModalOpen={isModalOpen}
          />
        )}
        {popularData.length > 0 && (
          <ContentRow
            title={`Popular${
              searchQuery
                ? ` (${popularData.length} result${
                    popularData.length > 1 ? "s" : ""
                  })`
                : ""
            }`}
            movies={popularData}
            onMovieClick={handleMovieClick}
            isModalOpen={isModalOpen}
          />
        )}
        {newReleasesData.length > 0 && (
          <ContentRow
            title={`New Releases${
              searchQuery
                ? ` (${newReleasesData.length} result${
                    newReleasesData.length > 1 ? "s" : ""
                  })`
                : ""
            }`}
            movies={newReleasesData}
            onMovieClick={handleMovieClick}
            isModalOpen={isModalOpen}
          />
        )}

        {continueWatchingData.length > 0 && (
          <ContentRow
            title="Continue Watching"
            movies={continueWatchingData}
            onMovieClick={handleMovieClick}
            isModalOpen={isModalOpen}
          />
        )}
        {myListData.length > 0 && (
          <ContentRow
            title="My List"
            movies={myListData}
            onMovieClick={handleMovieClick}
            isModalOpen={isModalOpen}
          />
        )}

        {/* Labels du thème actuel */}
        {filteredLabels.map((labelInfo) => {
          const categoryData = data?.[labelInfo.label];
          const rawLabelVideos = categoryData?.videos || [];
          const labelVideos = filterVideosBySearch(rawLabelVideos, searchQuery);

          // Ne montrer la ligne que si nous avons des vidéos pour ce label après filtrage
          return (
            labelVideos.length > 0 && (
              <ContentRow
                key={labelInfo.id}
                title={`${labelInfo.title}${
                  searchQuery
                    ? ` (${labelVideos.length} result${
                        labelVideos.length > 1 ? "s" : ""
                      })`
                    : ""
                }`}
                movies={labelVideos}
                onMovieClick={handleMovieClick}
                isModalOpen={isModalOpen}
              />
            )
          );
        })}
      </div>

      {/* Footer spacing */}
      <div className="h-20 bg-black"></div>

      {/* Theme Sidebar */}
      <ThemeSidebar />

      {/* Standup Invite Modal */}
      <RegistrationInviteModal
        open={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
      />

      {/* Movie Modal */}
      <MovieModal
        movie={selectedMovie}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        allVideos={Object.values(data || {}).flatMap((cat) => cat?.videos || [])}
        setSelectedMovie={setSelectedMovie}
      />
    </div>
  );
};

function App() {
  return (
    <LanguageProvider>
      <ThemeProvider>
        <VideoProvider>
          <div className="App">
            <BrowserRouter>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/:pathseo" element={<Home />} />
              </Routes>
            </BrowserRouter>
            {/* Composant SDK Mayan monté au niveau racine de l'application */}
          </div>
        </VideoProvider>
        {/* <MayanSDKContainer /> */}
      </ThemeProvider>
    </LanguageProvider>
  );
}

export default App;
